/**
 * Store Update Banner — bottom-right nudge for `optional` and `recommended` tiers.
 *
 * The mandatory tier renders through `StoreUpdateRequiredModal` instead; this component
 * intentionally does not handle it (single-responsibility, and a modal is a different
 * accessibility contract from a status banner).
 *
 * Copy patterns follow the existing OTA `UpdateBanner`:
 *   optional    → "Update available" + dismiss + CTA
 *   recommended → "Please update" + CTA only (no dismiss control — sticky by definition)
 *
 * The CTA opens the store URL in a new context via `window.open`. On iOS/Android that hands
 * off to Safari / Chrome, which then hand off to the App Store / Play Store app. Same pattern
 * the existing OTA banner uses for its APK CTA — no `@capacitor/browser` needed.
 */

import { ArrowDownToLine, X } from 'lucide-react';
import { useStoreUpdate } from '@/hooks/useStoreUpdate';

export function StoreUpdateBanner() {
  const { tier, storeUrl, latestVersion, dismiss } = useStoreUpdate();

  if (tier !== 'optional' && tier !== 'recommended') return null;
  if (!storeUrl) return null;

  const isRecommended = tier === 'recommended';

  const handleUpdate = () => {
    window.open(storeUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <div
      role={isRecommended ? 'alert' : 'status'}
      aria-live={isRecommended ? 'assertive' : 'polite'}
      // 6rem (~96px) clears the bottom nav's content + border; add env(safe-area-inset-bottom)
      // to also clear the gesture-bar padding the nav applies via `pb-[env(safe-area-inset-bottom)]`.
      // A fixed `bottom-24` worked on Pixel (small safe-area) but on Galaxy S24 Plus the taller
      // gesture-bar inset pushed the nav's top edge above 96px and covered this banner.
      className="fixed bottom-[calc(6rem+env(safe-area-inset-bottom))] right-4 z-50 max-w-sm"
    >
      <div className="bg-blue-600/95 backdrop-blur-sm text-white rounded-lg shadow-lg p-4">
        <div className="flex items-start gap-3">
          <ArrowDownToLine className="w-5 h-5 flex-shrink-0 mt-0.5" aria-hidden="true" />

          <div className="flex-1 min-w-0">
            <p className="font-semibold">
              {isRecommended ? 'Please update Trade Loop' : 'Update available'}
            </p>
            <p className="text-sm text-blue-100 mt-0.5">
              {isRecommended
                ? `A newer version${latestVersion ? ` (${latestVersion})` : ''} is available in the store. Update to keep the best experience.`
                : `Trade Loop${latestVersion ? ` ${latestVersion}` : ''} is available in the store.`}
            </p>

            <div className="flex items-center gap-2 mt-3">
              <button
                type="button"
                onClick={handleUpdate}
                className="bg-white text-blue-700 font-semibold text-sm px-3 py-1.5 rounded-md hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-blue-600"
              >
                Open store
              </button>
              {!isRecommended && (
                <button
                  type="button"
                  onClick={dismiss}
                  className="text-sm text-blue-100 px-2 py-1.5 rounded-md hover:text-white focus:outline-none focus:ring-2 focus:ring-white"
                >
                  Later
                </button>
              )}
            </div>
          </div>

          {!isRecommended && (
            <button
              type="button"
              onClick={dismiss}
              aria-label="Dismiss update notification"
              className="flex-shrink-0 text-blue-100 hover:text-white focus:outline-none focus:ring-2 focus:ring-white rounded"
            >
              <X className="w-4 h-4" aria-hidden="true" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default StoreUpdateBanner;
